import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import type { MaPosition, MaPositionDisposition } from "@/lib/material";
import {
  maPositionOrderHint,
  maPositionOrderState,
} from "@/pages/material/maListDisplay";

export interface MaPositionDispositionDialogProps {
  open: boolean;
  mode: MaPositionDisposition;
  maNummer: string;
  positionen: MaPosition[];
  /** Optional: vorselektierte Position(en), z. B. aus Zeilenaktion. */
  initialPositionIds?: number[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: (positionIds: number[], grund: string) => void;
}

function isSelectable(p: MaPosition): boolean {
  const state = maPositionOrderState(p);
  return (
    state !== "bestellt" &&
    state !== "geliefert" &&
    state !== "storniert" &&
    state !== "abgelehnt"
  );
}

export function MaPositionDispositionDialog({
  open,
  mode,
  maNummer,
  positionen,
  initialPositionIds,
  loading = false,
  onClose,
  onConfirm,
}: MaPositionDispositionDialogProps) {
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [grund, setGrund] = useState("");
  const [error, setError] = useState<string | null>(null);

  const selectable = useMemo(
    () => positionen.filter((p) => p.id != null && isSelectable(p)),
    [positionen],
  );

  useEffect(() => {
    if (!open) {
      setSelected({});
      setGrund("");
      setError(null);
      return;
    }
    const preset = new Set(initialPositionIds || []);
    const next: Record<number, boolean> = {};
    for (const p of selectable) {
      next[p.id] = preset.size > 0 ? preset.has(p.id) : true;
    }
    setSelected(next);
    setGrund("");
    setError(null);
  }, [open, selectable, initialPositionIds]);

  const selectedIds = useMemo(
    () => selectable.filter((p) => selected[p.id]).map((p) => p.id),
    [selectable, selected],
  );
  const allSelected =
    selectable.length > 0 && selectable.every((p) => selected[p.id]);

  const title = mode === "storniert" ? "Positionen stornieren" : "Positionen ablehnen";
  const actionLabel = mode === "storniert" ? "Stornieren" : "Ablehnen";
  const description =
    mode === "storniert"
      ? `Materialanforderung ${maNummer} – ausgewählte Positionen als storniert markieren.`
      : `Materialanforderung ${maNummer} – ausgewählte Positionen als abgelehnt markieren.`;

  function toggleAll(checked: boolean) {
    const next: Record<number, boolean> = {};
    for (const p of selectable) next[p.id] = checked;
    setSelected(next);
  }

  function handleConfirm() {
    const trimmed = grund.trim();
    if (!selectedIds.length) {
      setError("Bitte mindestens eine Position auswählen.");
      return;
    }
    if (!trimmed) {
      setError("Bitte eine Begründung eingeben.");
      return;
    }
    setError(null);
    onConfirm(selectedIds, trimmed);
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? () => undefined : onClose}
      dirty={selectedIds.length > 0 || grund.trim().length > 0}
      size="lg"
      title={title}
      description={description}
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} variant="ghost" />
          <Button
            variant="danger"
            loading={loading}
            disabled={loading || selectedIds.length === 0 || !grund.trim()}
            onClick={handleConfirm}
          >
            {selectedIds.length === 1
              ? `1 Position ${actionLabel.toLowerCase()}`
              : `${selectedIds.length || ""} Positionen ${actionLabel.toLowerCase()}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-label-secondary">
          Bereits bestellte Positionen können hier nicht geändert werden. Stornierte oder
          abgelehnte Positionen zählen für den Monteur nicht mehr als offen.
        </p>

        <div className="space-y-1">
          <label className="eh-label" htmlFor="ma-pos-disposition-grund">
            Begründung<span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="ma-pos-disposition-grund"
            className="eh-input min-h-[4.5rem]"
            rows={3}
            placeholder="Bitte kurz begründen …"
            value={grund}
            disabled={loading}
            onChange={(e) => setGrund(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-label">Positionen</p>
          {selectable.length > 0 ? (
            <button
              type="button"
              className="text-xs text-accent hover:underline"
              onClick={() => toggleAll(!allSelected)}
            >
              {allSelected ? "Keine auswählen" : "Alle auswählen"}
            </button>
          ) : null}
        </div>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {selectable.length === 0 ? (
          <p className="rounded-ios border border-separator bg-fill-quaternary/15 px-3 py-2 text-sm text-label-secondary">
            Keine stornierbaren / ablehnbaren Positionen vorhanden.
          </p>
        ) : (
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {positionen.map((p, idx) => {
              const state = maPositionOrderState(p);
              const selectableRow = isSelectable(p);
              const hint = maPositionOrderHint(p);
              const checked = !!selected[p.id];
              const menge =
                p.menge != null && Number.isFinite(Number(p.menge))
                  ? String(p.menge)
                  : "–";
              const einheit = (p.einheit || "").trim();
              return (
                <label
                  key={p.id}
                  className={`flex items-start gap-2 rounded-ios border px-3 py-2 text-sm ${
                    selectableRow
                      ? "cursor-pointer border-separator bg-card"
                      : "cursor-not-allowed border-separator bg-fill-quaternary/10 opacity-70"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                    checked={selectableRow ? checked : false}
                    disabled={!selectableRow || loading}
                    onChange={(e) =>
                      setSelected((prev) => ({ ...prev, [p.id]: e.target.checked }))
                    }
                  />
                  <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
                    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-label-secondary">#{idx + 1}</span>
                      <span className="font-medium text-label">
                        {menge}
                        {einheit ? ` ${einheit}` : ""}
                      </span>
                      <span className="min-w-0 text-label">{p.text || "–"}</span>
                      <span className="text-xs text-label-tertiary">({state})</span>
                    </span>
                    {hint ? (
                      <span className="mt-0.5 block text-xs text-label-secondary">{hint}</span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
}
